import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, Radio, Tooltip } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useContext, useEffect, useMemo } from 'react';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { useHotelSpacesTypes } from 'app/hooks/data/useHotelSpacesTypes';
import {
  NOT_APPLICABLE_OPENING_HOURS_TYPE,
  OpeningHoursType,
} from 'app/typings/core/admin/experiences/openingHours/openingHours';
import { ExperienceFieldType } from 'app/typings/experiences';
import { isDeepEqual } from 'app/utils/object';
import { slugify } from 'app/utils/strings';

import { OpeningHoursDaily } from './OpeningHoursDaily';
import { OpeningHoursWeekly } from './OpeningHoursWeekly';
import './openingHours.scss';
import { normalizeOpeningHours, presetOpeningHours } from './utils';

const RULES = [{ required: true, message: 'Required' }];

const RADIO_OPTIONS = [
  { label: 'Same every day', value: OpeningHoursType.WEEKLY },
  { label: 'Depends on the day', value: OpeningHoursType.DAILY },
];

export const OpeningHours = () => {
  const form = Form.useFormInstance();
  const { isViewMode } = useContext(ViewEditModeContext);

  const actualType = Form.useWatch(['openingHours', 'type'], form);
  const spaceIdsRaw = Form.useWatch(['spaceIds'], form);
  const hotelId = Form.useWatch(['hotelId'], form);
  const categoryId = Form.useWatch(['categoryId'], form);

  const { data: spaceTypes } = useHotelSpacesTypes();
  const { data: experienceCategories } = useExperiencesCategories();
  const { data: hotel } = useGetHotel(hotelId);

  const category = experienceCategories?.find((c) => c.id === categoryId);
  const spaceIds = useMemo(() => spaceIdsRaw ?? [], [spaceIdsRaw]);

  const presetHours = useMemo(
    () => presetOpeningHours(hotel?.spaces, spaceIds, spaceTypes),
    [hotel?.spaces, spaceIds, spaceTypes]
  );

  const selectedPreset = useMemo(
    () => presetHours.find((preset) => preset.name === actualType),
    [presetHours, actualType]
  );

  useEffect(() => {
    if (!presetHours.length) {
      return;
    }

    const currentOH = form.getFieldValue(['openingHours']);

    if (
      !currentOH ||
      !Object.values(OpeningHoursType).includes(
        currentOH.type as OpeningHoursType
      )
    ) {
      return;
    }

    const current = normalizeOpeningHours(currentOH);
    const matching = presetHours.find((preset) =>
      isDeepEqual(preset.openingHours, current)
    );

    if (matching) {
      form.setFieldValue(['openingHours', 'type'], matching.name);
    }
  }, [presetHours, form]);

  const applyPreset = (event: RadioChangeEvent) => {
    const preset = presetHours.find(({ name }) => name === event.target.value);

    if (preset) {
      form.setFieldValue(['openingHours'], {
        ...preset.openingHours,
        type: preset.name,
      });
    }
  };

  const displayedType = selectedPreset?.openingHours.type ?? actualType;
  const isWeeklyPreset = displayedType === OpeningHoursType.WEEKLY;
  const isDailyPreset = displayedType === OpeningHoursType.DAILY;

  return (
    <FormLineLayout
      label={
        <span className="opening-hours-label">
          Opening hours
          <Tooltip title="If added to a day package, this activity's opening hours will be limited to the day package's start and end times.">
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      }
      isRequired
      labelOnTop
      disabled={isViewMode}
    >
      <div className="opening-hours-wrapper">
        <Form.Item name={['openingHours']} className="opening-hours-form">
          <Form.Item name={['openingHours', 'type']} rules={RULES} noStyle>
            <Radio.Group disabled={isViewMode} onChange={applyPreset}>
              {presetHours.map((preset) => (
                <Radio key={slugify(preset.name)} value={preset.name}>
                  {`Same as ${preset.name}`}
                </Radio>
              ))}
              {RADIO_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
              {category?.openingHours === ExperienceFieldType.OPTIONAL && (
                <Radio value={NOT_APPLICABLE_OPENING_HOURS_TYPE}>
                  Not applicable
                </Radio>
              )}
            </Radio.Group>
          </Form.Item>
        </Form.Item>
        {isWeeklyPreset && <OpeningHoursWeekly />}
        {isDailyPreset && <OpeningHoursDaily />}
      </div>
    </FormLineLayout>
  );
};
